/**
 * Student Payment History Page
 * View all payment transactions
 */

import React from 'react';
import { Navbar, Footer } from '../../components';
import { formatDate } from '../../utils/dateUtils';
import SEO from '../../components/common/SEO';

// Sample payment data
const payments = [
  {
    id: 'PAY-001',
    course: 'Complete React.js Masterclass',
    amount: 49.99,
    date: '2026-01-05',
    status: 'completed',
    method: 'Credit Card',
    cardLast4: '4242',
  },
  {
    id: 'PAY-002',
    course: 'Python for Data Science',
    amount: 0,
    date: '2025-12-20',
    status: 'completed',
    method: 'Free Enrollment',
    cardLast4: null,
  },
  {
    id: 'PAY-003',
    course: 'Machine Learning A-Z',
    amount: 79.99,
    date: '2025-12-15',
    status: 'completed',
    method: 'Credit Card',
    cardLast4: '4242',
  },
  {
    id: 'PAY-004',
    course: 'JavaScript Fundamentals',
    amount: 29.99,
    date: '2025-11-10',
    status: 'completed',
    method: 'PayPal',
    cardLast4: null,
  },
];

const StudentPaymentHistory = () => {
  const totalSpent = payments.reduce((sum, p) => sum + p.amount, 0);

  return (
    <div className="lh-payment-history min-h-screen bg-dark-50">
      <SEO title="Payment History" noIndex />
      <Navbar />

      <main className="lh-payment-history__main pt-20 pb-12">
        <div className="lh-payment-history__container max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="lh-payment-history__header mb-8">
            <h1 className="lh-payment-history__title text-2xl md:text-3xl font-bold text-dark-900 mb-2">
              Payment History
            </h1>
            <p className="lh-payment-history__subtitle text-dark-500">
              View all your payment transactions
            </p>
          </div>

          {/* Summary Cards */}
          <div className="lh-payment-history__summary grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
            <div className="lh-payment-history__summary-card lh-payment-history__summary-card--total card p-5">
              <div className="lh-payment-history__summary-content flex items-center gap-4">
                <div className="lh-payment-history__summary-icon w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-primary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="lh-payment-history__summary-info">
                  <p className="lh-payment-history__summary-value text-2xl font-bold text-dark-900">${totalSpent.toFixed(2)}</p>
                  <p className="lh-payment-history__summary-label text-sm text-dark-500">Total Spent</p>
                </div>
              </div>
            </div>
            <div className="lh-payment-history__summary-card lh-payment-history__summary-card--transactions card p-5">
              <div className="lh-payment-history__summary-content flex items-center gap-4">
                <div className="lh-payment-history__summary-icon w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="lh-payment-history__summary-info">
                  <p className="lh-payment-history__summary-value text-2xl font-bold text-dark-900">{payments.length}</p>
                  <p className="lh-payment-history__summary-label text-sm text-dark-500">Transactions</p>
                </div>
              </div>
            </div>
            <div className="lh-payment-history__summary-card lh-payment-history__summary-card--paid card p-5">
              <div className="lh-payment-history__summary-content flex items-center gap-4">
                <div className="lh-payment-history__summary-icon w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center">
                  <svg className="w-6 h-6 text-secondary-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
                <div className="lh-payment-history__summary-info">
                  <p className="lh-payment-history__summary-value text-2xl font-bold text-dark-900">{payments.filter(p => p.amount > 0).length}</p>
                  <p className="lh-payment-history__summary-label text-sm text-dark-500">Paid Courses</p>
                </div>
              </div>
            </div>
          </div>

          {/* Payments Table */}
          <div className="lh-payment-history__table card overflow-hidden">
            <div className="lh-payment-history__table-header p-5 border-b border-dark-100">
              <h2 className="lh-payment-history__table-title text-lg font-bold text-dark-900">All Transactions</h2>
            </div>
            
            {/* Desktop Table */}
            <div className="lh-payment-history__desktop hidden md:block overflow-x-auto">
              <table className="lh-payment-history__table-element w-full">
                <thead className="lh-payment-history__table-head bg-dark-50">
                  <tr className="lh-payment-history__table-row">
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Transaction ID</th>
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Course</th>
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Date</th>
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Payment Method</th>
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Amount</th>
                    <th className="lh-payment-history__table-th text-left p-4 text-sm font-semibold text-dark-600">Status</th>
                  </tr>
                </thead>
                <tbody className="lh-payment-history__table-body divide-y divide-dark-100">
                  {payments.map((payment) => (
                    <tr key={payment.id} className="lh-payment-history__payment-row hover:bg-dark-50">
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--id p-4 text-sm font-medium text-dark-900">{payment.id}</td>
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--course p-4 text-sm text-dark-600">{payment.course}</td>
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--date p-4 text-sm text-dark-500">{formatDate(payment.date)}</td>
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--method p-4 text-sm text-dark-600">
                        {payment.method}
                        {payment.cardLast4 && (
                          <span className="lh-payment-history__payment-card text-dark-400"> •••• {payment.cardLast4}</span>
                        )}
                      </td>
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--amount p-4 text-sm font-semibold text-dark-900">
                        {payment.amount > 0 ? `$${payment.amount.toFixed(2)}` : 'Free'}
                      </td>
                      <td className="lh-payment-history__payment-cell lh-payment-history__payment-cell--status p-4">
                        <span className="lh-payment-history__payment-badge inline-flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-full bg-green-100 text-green-600">
                          <svg className="lh-payment-history__payment-badge-icon w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="lh-payment-history__payment-badge-text">Completed</span>
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lh-payment-history__mobile md:hidden divide-y divide-dark-100">
              {payments.map((payment) => (
                <div key={payment.id} className="lh-payment-history__mobile-card p-4">
                  <div className="lh-payment-history__mobile-header flex justify-between items-start mb-2">
                    <span className="lh-payment-history__mobile-id text-xs font-medium text-dark-400">{payment.id}</span>
                    <span className="lh-payment-history__mobile-badge inline-flex items-center gap-1 text-xs font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-600">
                      Completed
                    </span>
                  </div>
                  <h4 className="lh-payment-history__mobile-course font-medium text-dark-900 mb-1">{payment.course}</h4>
                  <p className="lh-payment-history__mobile-date text-sm text-dark-500 mb-2">{formatDate(payment.date)}</p>
                  <div className="lh-payment-history__mobile-footer flex justify-between items-center">
                    <span className="lh-payment-history__mobile-method text-sm text-dark-600">
                      {payment.method}
                      {payment.cardLast4 && ` •••• ${payment.cardLast4}`}
                    </span>
                    <span className="lh-payment-history__mobile-amount font-bold text-dark-900">
                      {payment.amount > 0 ? `$${payment.amount.toFixed(2)}` : 'Free'}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default StudentPaymentHistory;
